'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Skeleton } from '@material-bank/mb3-components'
import { areImagesWarm, markImagesWarm } from '@material-bank/mtag-utilities'

type LoadingImageProps = {
  src: string
  srcSet?: string
  alt: string
  // Applied to the <img> (size, object-fit, corners, etc.).
  className?: string
  // Applied to the wrapping <span>. Use to make the image fill a sized parent
  // (e.g. `block h-full w-full`) instead of shrink-wrapping its content.
  wrapperClassName?: string
  // Applied to the skeleton overlay — usually the matching corner radius.
  skeletonClassName?: string
  // Native lazy/eager hint forwarded to the <img>.
  loading?: 'lazy' | 'eager'
  draggable?: boolean
}

// An image that shows a skeleton placeholder until it settles, then fades in.
// Already-cached images (where neither `onLoad` nor `onError` fires) are resolved
// via the element's `complete` flag. Shared by the gallery and colorway/option
// swatches so the loading treatment stays consistent.
//
// A source this session has already loaded (on a previous mount of this tile, or
// decoded ahead of time by `preloadImages`) is "warm" and never fades in. An
// eager warm image also mounts without its skeleton. A lazy warm one keeps the
// skeleton until the browser actually fetches it, since a lazy <img> holds no
// bitmap until it nears the viewport and would otherwise show as a blank card.
// Without the record a grid the user returns to flashes every tile through the
// cold treatment again — and on WebKit, which resolves cached images
// asynchronously, the skeleton paints for a frame or more before the bitmap.

type ImageSource = { src: string; srcSet?: string }

function resolveUrl(url: string): string {
  try {
    return new URL(url, document.baseURI).href
  } catch {
    return url
  }
}

// Whether `currentSrc` is one of the candidates this source renders. Right after
// a `src` swap the element can still report the previous image as complete, so
// only a match may record the new source as warm.
function isCurrentSource(el: HTMLImageElement, { src, srcSet }: ImageSource) {
  const candidates = [
    src,
    ...(srcSet ?? '').split(',').map((entry) => entry.trim().split(/\s+/)[0]),
  ].filter(Boolean)

  return candidates.some((candidate) => resolveUrl(candidate) === el.currentSrc)
}

export function LoadingImage({
  src,
  srcSet,
  alt,
  className,
  wrapperClassName,
  skeletonClassName,
  loading,
  draggable,
}: LoadingImageProps) {
  const [warm, setWarm] = useState(() => areImagesWarm([{ src, srcSet }]))
  // A broken image never fires `onLoad`, and a cached broken one never fires
  // `onError` either — it arrives `complete` with `naturalWidth === 0`. Without an
  // error state the skeleton shimmers forever over an invisible <img>.
  const [settled, setSettled] = useState(() => warm && loading !== 'lazy')
  const imageElementRef = useRef<HTMLImageElement | null>(null)
  const previousSrc = useRef(src)
  // Read by the stable callbacks below, so a source change never changes the
  // ref callback's identity — React would detach and reattach the ref and run
  // the settle check against the element mid-swap.
  const source = useRef<ImageSource>({ src, srcSet })
  source.current = { src, srcSet }

  const loadHandler = useCallback(() => {
    markImagesWarm([source.current])
    setWarm(true)
    setSettled(true)
  }, [])

  // `complete` alone is not enough — it is also true for an <img> with no usable
  // source. Pair it with `currentSrc`, which is set once a source has actually been
  // selected: non-empty for a loaded image and for a 404 (both must settle), empty
  // for no source (must not). Deliberately not `src`: that is the resolved IDL
  // attribute, so a present-but-empty `src=""` reads truthy. React strips an empty
  // `src` rather than rendering it, so that case cannot arise here today — this
  // keeps the guard from depending on that.
  const checkSettled = useCallback(
    (el: HTMLImageElement | null) => {
      if (!el?.complete || !el.currentSrc) {
        return
      }

      if (el.naturalWidth > 0 && isCurrentSource(el, source.current)) {
        loadHandler()

        return
      }

      setSettled(true)
    },
    [loadHandler],
  )

  // Stable identity so React doesn't detach and reattach the ref on every render.
  const imageRef = useCallback(
    (el: HTMLImageElement | null) => {
      imageElementRef.current = el
      checkSettled(el)
    },
    [checkSettled],
  )

  // The ref callback only fires on mount, so a `src` swapped in place on an already
  // mounted <img> (this instance is reused widely via SwatchTile) would keep the
  // stale `settled = true`: skeleton suppressed and the image at opacity-100 while
  // the new bytes are still fetching. Reset on every real `src` change so the
  // skeleton returns until the new source loads — unless it is already cached, in
  // which case it settles with no load event and we re-run the mount-time guard.
  useEffect(() => {
    if (previousSrc.current === src) {
      return
    }

    previousSrc.current = src
    const nextWarm = areImagesWarm([{ src, srcSet }])
    setWarm(nextWarm)
    setSettled(nextWarm && loading !== 'lazy')
    checkSettled(imageElementRef.current)
  }, [src, srcSet, loading, checkSettled])

  const opacityClassName = warm
    ? 'opacity-100'
    : `transition-opacity duration-300 ${settled ? 'opacity-100' : 'opacity-0'}`

  return (
    <span
      className={`relative inline-block${
        wrapperClassName ? ` ${wrapperClassName}` : ''
      }`}
    >
      {settled ? null : (
        <Skeleton
          className={`absolute inset-0 h-full w-full${
            skeletonClassName ? ` ${skeletonClassName}` : ''
          }`}
          data-testid="loading-image-skeleton"
        />
      )}
      <img
        ref={imageRef}
        src={src}
        srcSet={srcSet}
        alt={alt}
        draggable={draggable}
        loading={loading}
        onLoad={loadHandler}
        onError={() => setSettled(true)}
        className={`${opacityClassName}${className ? ` ${className}` : ''}`}
      />
    </span>
  )
}
